import { imageVariantLink } from '@/common/file-variants';

import { SeatType } from './people.types';
import type {
  AddContactsRequestDto,
  AssignSeatDto,
  AssignSeatInput,
  ChangePreferencesRequestDto,
  Contact,
  ContactDto,
  NewContact,
  NewContactDto,
  PageDto,
  PeoplePage,
  Person,
  PersonDto,
  PersonInfo,
  PersonInfoDto,
  PersonPreferences,
  PersonPreferencesDto,
  PersonService,
  PersonServiceDto,
  RemoveSeatDto,
  RemoveSeatInput,
  UpdateContactInput,
  UpdateContactRequestDto,
  UpdatePersonInput,
  UpdatePersonRequestDto,
} from './people.types';

export const mapContact = (dto: ContactDto): Contact => ({
  id: dto.id,
  contact: dto.contact,
  contactType: dto.contact_type,
});

export const mapPersonInfo = (dto: PersonInfoDto): PersonInfo => ({
  firstName: dto.first_name,
  lastName: dto.last_name,
  birthday: dto.birthday,
  gender: dto.gender,
});

export const mapPreferences = (dto: PersonPreferencesDto): PersonPreferences => ({
  dietaryRestrictions: dto.dietary_restrictions ?? [],
  favoriteDrinks: dto.favorite_drinks ?? [],
  favoriteFoods: dto.favorite_foods ?? [],
  roomPreferences: dto.room_preferences ?? [],
  seasicknessNotes: dto.seasickness_notes ?? '',
});

// `seat_type` is a bare string on the wire; an unrecognised one reads the same as none
// rather than being cast into the enum.
const parseSeatType = (raw: string | null | undefined): SeatType | null =>
  raw && (Object.values(SeatType) as string[]).includes(raw) ? (raw as SeatType) : null;

export const mapPersonService = (dto: PersonServiceDto): PersonService => ({
  personId: dto.person_id,
  serviceId: dto.service_id,
  serviceType: dto.service_type,
  role: dto.role,
  rights: dto.rights ?? [],
  presence: dto.presence,
  joinDate: dto.join_date,
  seatType: parseSeatType(dto.seat_type),
});

/** The largest box an avatar is drawn in (seat modals), so one variant serves every site. */
const AVATAR_BOX = 34;

export const mapPerson = (dto: PersonDto): Person => ({
  id: dto.id,
  activationStatus: dto.activation_status,
  blockStatus: dto.block_status,
  onboarded: dto.onboarded,
  avatar: imageVariantLink(dto.avatar, AVATAR_BOX) ?? '',
  personInfo: mapPersonInfo(dto.person_info),
  contacts: (dto.contacts ?? []).map(mapContact),
  preferences: dto.preferences ? mapPreferences(dto.preferences) : null,
  services: (dto.services ?? []).map(mapPersonService),
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const mapPeoplePage = (dto: PageDto<PersonDto>): PeoplePage => ({
  count: dto.count,
  items: (dto.items ?? []).map(mapPerson),
});

export const mapUpdatePersonInputToDto = (input: UpdatePersonInput): UpdatePersonRequestDto => ({
  first_name: input.firstName,
  last_name: input.lastName,
  birthday: input.birthday,
  gender: input.gender,
});

export const mapNewContactToDto = (contact: NewContact): NewContactDto => ({
  contact: contact.contact,
  contact_type: contact.contactType,
});

export const mapAddContactsToDto = (contacts: NewContact[]): AddContactsRequestDto => ({
  contacts: contacts.map(mapNewContactToDto),
});

export const mapUpdateContactInputToDto = (input: UpdateContactInput): UpdateContactRequestDto => ({
  id: input.id,
  contact: input.contact,
  contact_type: input.contactType,
});

export const mapAssignSeatToDto = (input: AssignSeatInput): AssignSeatDto => ({
  person_id: input.personId,
  service_id: input.serviceId,
  seat_type: input.seatType,
});

export const mapRemoveSeatToDto = (input: RemoveSeatInput): RemoveSeatDto => ({
  person_id: input.personId,
  service_id: input.serviceId,
});

export const mapPreferencesToDto = (
  preferences: PersonPreferences,
): ChangePreferencesRequestDto => ({
  preferences: {
    dietary_restrictions: preferences.dietaryRestrictions,
    favorite_drinks: preferences.favoriteDrinks,
    favorite_foods: preferences.favoriteFoods,
    room_preferences: preferences.roomPreferences,
    seasickness_notes: preferences.seasicknessNotes,
  },
});
